import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Divider,
  Select,
  MenuItem,
  Alert,
  Snackbar,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import { useCookies } from "react-cookie";
import Layout from "../components/layout/GSTlayout";
import config from "../config.js";

const GSTCompliancePage = () => {
  const [complianceItems, setComplianceItems] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [cookies] = useCookies(["userId"]);
  const { API_BASE_URL } = config;

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      category: "",
      title: "",
      dueDate: "",
      priority: "low",
    }
  });

  useEffect(() => {
    if (cookies.userId) {
      fetchComplianceItems();
    } else {
      setError("User authentication required");
      setLoading(false);
    }
  }, [cookies.userId]);

  const fetchComplianceItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/v1/getCompliceItems?userId=${cookies.userId}`);
      
      // Transform the data structure to match UI expectations
      const transformedItems = response.data.data.flatMap(category => 
        category.items.map(item => ({
          id: item._id,
          category: category.category,
          title: item.title,
          completed: item.completed,
          dueDate: item.dueDate,
          priority: item.priority
        }))
      );
      
      setComplianceItems(transformedItems);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch compliance items");
      setComplianceItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleItemToggle = async (id) => {
    try {
      await axios.put(`${API_BASE_URL}/api/v1/updateCompliceItems/${id}`, {
        completed: !complianceItems.find(item => item.id === id).completed,
        userId: cookies.userId
      });
      fetchComplianceItems();
      showSnackbar("Item updated successfully", "success");
    } catch (error) {
      showSnackbar("Failed to update item", "error");
    }
  };

  const onSubmit = async (data) => {
    try {
      await axios.post(`${API_BASE_URL}/api/v1/createCompliceItems`, {
        ...data,
        userId: cookies.userId
      });
      setOpenDialog(false);
      reset();
      fetchComplianceItems();
      showSnackbar("Item added successfully", "success");
    } catch (error) {
      showSnackbar("Failed to add item", "error");
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const renderPriorityChip = (priority) => {
    const configs = {
      high: { icon: <ErrorIcon />, color: "error" },
      medium: { icon: <WarningIcon />, color: "warning" },
      low: { icon: <CheckCircleIcon />, color: "success" }
    };
    const config = configs[priority] || configs.low;
    return <Chip icon={config.icon} label={priority.toUpperCase()} color={config.color} size="medium" />;
  };

  const calculateCompliancePercentage = () => {
    if (!Array.isArray(complianceItems)) return 0;
    const totalItems = complianceItems.length;
    const completedItems = complianceItems.filter(item => item?.completed).length;
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Card sx={{ maxWidth: 1200, margin: "auto" }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h4">GST Compliance Checklist</Typography>
              </Grid>
              <Grid item>
                <Typography variant="h6">
                  Overall Compliance: {calculateCompliancePercentage()}%
                </Typography>
              </Grid>
            </Grid>

            {loading ? (
              <Typography>Loading...</Typography>
            ) : (
              Object.entries(complianceItems.reduce((acc, item) => {
                if (!acc[item.category]) acc[item.category] = [];
                acc[item.category].push(item);
                return acc;
              }, {})).map(([category, items]) => (
                <motion.div
                  key={category}
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ marginBottom: 16 }}
                >
                  <Typography variant="h6" gutterBottom>{category}</Typography>
                  {items.map((item) => (
                    <motion.div
                      key={item.id}
                      whileHover={{ scale: 1.01 }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: 8,
                        backgroundColor: item.completed ? "#e8f5e9" : "#fff3e0",
                        borderRadius: 4,
                        marginBottom: 8
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={item.completed}
                            onChange={() => handleItemToggle(item.id)}
                            color="primary"
                          />
                        }
                        label={
                          <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={6}>
                              <Typography>{item.title}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body2" color="textSecondary">
                                Due: {item.dueDate}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              {renderPriorityChip(item.priority)}
                            </Grid>
                          </Grid>
                        }
                      />
                    </motion.div>
                  ))}
                  <Divider />
                </motion.div>
              ))
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
              sx={{ marginTop: 2 }}
            >
              Add Compliance Item
            </Button>
          </CardContent>
        </Card>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Add New Compliance Item</DialogTitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    autoFocus
                    margin="dense"
                    label="Category"
                    fullWidth
                    sx={{ marginBottom: 2 }}
                  />
                )}
              />
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="dense"
                    label="Item Title"
                    fullWidth
                    sx={{ marginBottom: 2 }}
                  />
                )}
              />
              <Controller
                name="dueDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="dense"
                    label="Due Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ marginBottom: 2 }}
                  />
                )}
              />
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <Select {...field} fullWidth sx={{ marginBottom: 2 }}>
                    {["low", "medium", "high"].map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority.charAt(0).toUpperCase() + priority.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
              <Button type="submit">Add Item</Button>
            </DialogActions>
          </form>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Layout>
  );
};

export default GSTCompliancePage;
